<script setup lang="ts">
import { declareReplacement } from '@/shared/state/leadIntent'
import { scrollTo } from '../store'
// Pixabay Content License (free commercial use, no attribution required).
import heroPhoto from '@/assets/shouldi-hero-ac.jpg'

const STATS = [
    { value: '$450–$2,000', label: 'typical AC repair' },
    { value: '$6k–$12k', label: 'typical replacement' },
    { value: '15–20 yrs', label: 'typical lifespan' },
]

// Halftone dot field, shrinking toward the top-right — scoreboard texture.
const DOTS = Array.from({ length: 8 }, (_, row) =>
    Array.from({ length: 12 }, (_, col) => ({
        cx: (col + 1) * 24,
        cy: (row + 1) * 24,
        r: Math.max(4.2 - row * 0.45 - col * 0.16, 0.6),
    })),
).flat()
</script>

<template>
    <section class="site-hero site-section">
        <div class="decor" aria-hidden="true">
            <svg class="stripes" viewBox="0 0 420 420" fill="none">
                <rect x="240" y="-80" width="34" height="420" rx="17" transform="rotate(35 240 -80)" fill="#17348c" fill-opacity="0.1" />
                <rect x="310" y="-100" width="34" height="440" rx="17" transform="rotate(35 310 -100)" fill="#1d4ed8" fill-opacity="0.12" />
                <rect x="380" y="-120" width="34" height="460" rx="17" transform="rotate(35 380 -120)" fill="#d64541" fill-opacity="0.12" />
            </svg>
            <svg class="halftone" viewBox="0 0 320 220" fill="#17348c">
                <circle
                    v-for="(dot, i) in DOTS"
                    :key="i"
                    :cx="dot.cx"
                    :cy="dot.cy"
                    :r="dot.r"
                    fill-opacity="0.14"
                />
            </svg>
        </div>
        <div class="site-content hero-grid">
            <div>
                <span class="kicker">The two-minute answer · Northeast Florida</span>
                <h1>Should you replace<br />your AC?<span class="accent"> Let's find out.</span></h1>
                <p class="lede muted">
                    Six questions. Published math — the same rules technicians use. A straight
                    verdict with every factor shown. No account, no AI, no games.
                </p>
                <div class="actions">
                    <button class="cta big" type="button" @click="scrollTo('wizard')">
                        Get my answer →
                    </button>
                    <button class="cta cta--secondary" type="button" @click="scrollTo('math')">
                        See the math first
                    </button>
                    <button
                        class="cta cta--quiet"
                        type="button"
                        @click="declareReplacement('contact')"
                    >
                        I already know I need a replacement →
                    </button>
                </div>

                <div class="stats">
                    <div v-for="stat in STATS" :key="stat.label" class="stat">
                        <span class="value">{{ stat.value }}</span>
                        <span class="label muted">{{ stat.label }}</span>
                    </div>
                </div>
            </div>
            <figure class="hero-photo">
                <img
                    :src="heroPhoto"
                    alt="A man relaxing on a sofa, pointing a remote at a sleek wall-mounted air conditioner"
                    width="1280"
                    height="853"
                />
                <figcaption>THE RIGHT CALL FEELS LIKE THIS</figcaption>
            </figure>
        </div>
    </section>
</template>

<style scoped lang="scss">
.stripes {
    position: absolute;
    top: 0;
    right: 0;
    width: min(32vw, 420px);
    height: auto;
}

.halftone {
    position: absolute;
    bottom: 2rem;
    left: -1rem;
    width: min(26vw, 300px);
    height: auto;
}

.hero-grid {
    display: grid;
    grid-template-columns: minmax(0, 6fr) minmax(0, 4fr);
    gap: var(--site-space-2xl);
    align-items: center;

    @media (max-width: 940px) {
        grid-template-columns: 1fr;
    }
}

.hero-photo {
    margin: 0;

    img {
        display: block;
        width: 100%;
        height: auto;
        border: 2px solid var(--site-text);
        border-radius: var(--site-radius-md);
        box-shadow: var(--site-shadow-lg);
    }

    figcaption {
        margin-top: var(--site-space-sm);
        font-size: 0.72rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        color: var(--site-text-muted);
        text-align: center;
    }

    @media (max-width: 940px) {
        display: none;
    }
}

.kicker {
    display: inline-block;
    font-weight: 700;
    font-size: 0.8rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--site-accent);
    margin-bottom: var(--site-space-md);
}

h1 .accent {
    color: var(--site-accent);
}

.lede {
    font-size: 1.2rem;
    max-width: 34rem;
    margin-bottom: var(--site-space-lg);
}

.actions {
    display: flex;
    gap: var(--site-space-sm);
    flex-wrap: wrap;
    align-items: center;
}

.big {
    font-size: 1.15rem;
}

.stats {
    display: flex;
    gap: var(--site-space-xl);
    flex-wrap: wrap;
    margin-top: var(--site-space-2xl);
    padding-top: var(--site-space-lg);
    border-top: 2px solid var(--site-text);

    .stat {
        display: flex;
        flex-direction: column;

        .value {
            font-family: var(--site-font-heading);
            font-size: clamp(1.4rem, 3.5vw, 2rem);
            font-variant-numeric: tabular-nums;
        }

        .label {
            font-size: 0.85rem;
            text-transform: uppercase;
            letter-spacing: 0.06em;
        }
    }
}
</style>
